import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { buildTaskProgressMap, getProjectProgressBreakdown } from '../utils/projectProgress';
import useBreakpoint from '../hooks/useBreakpoint';
import { pageMobile, TAP_TARGET } from '../mobileStyles';

const STATUS_TABS = [
  { value: 'all',        label: 'Todos' },
  { value: 'En curso',   label: 'En curso' },
  { value: 'Pendiente',  label: 'Pendiente' },
  { value: 'Pausado',    label: 'Pausado' },
  { value: 'Completado', label: 'Completado' },
];

function getStatusStyle(status) {
  switch (status) {
    case 'En curso':     return { background: 'rgba(107,28,42,0.08)', color: '#6B1C2A' };
    case 'Completado':   return { background: 'rgba(74,124,89,0.08)', color: '#4A7C59' };
    case 'Inicio':       return { background: 'rgba(201,169,110,0.12)', color: '#A08040' };
    case 'Pendiente':    return { background: 'rgba(201,169,110,0.12)', color: '#A08040' };
    case 'Diseño':       return { background: 'rgba(201,169,110,0.12)', color: '#A08040' };
    case 'Planificación':return { background: 'rgba(107,107,107,0.08)', color: '#6B6B6B' };
    case 'Pausado':      return { background: 'rgba(184,59,59,0.08)', color: '#B83B3B' };
    default:             return { background: '#f0f0f0', color: '#666' };
  }
}

function getProgressColor(p) {
  if (p === 100) return '#4A7C59';
  if (p >= 60) return '#6B1C2A';
  if (p >= 30) return '#C9A96E';
  return '#C4922A';
}

function yearOf(dateStr) {
  if (!dateStr) return null;
  const m = String(dateStr).slice(0, 4);
  const n = parseInt(m, 10);
  return Number.isFinite(n) ? n : null;
}

export default function GanttProjects({ onOpen } = {}) {
  const { isMobile, isTablet } = useBreakpoint();
  const [projects, setProjects] = useState([]);
  const [taskProgressMap, setTaskProgressMap] = useState({});
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    // Progress comes from the project's tareas, not from proyectos.progreso —
    // that column is legacy and nothing keeps it up to date.
    const [projRes, taskRes] = await Promise.all([
      supabase.from('proyectos').select('*').order('created_at', { ascending: false }),
      supabase.from('tareas').select('proyecto_id, progreso, tipo'),
    ]);
    if (projRes.data) setProjects(projRes.data);
    if (taskRes.error) {
      console.warn('[gantt-projects] task progress fetch failed:', taskRes.error.message);
    } else {
      setTaskProgressMap(buildTaskProgressMap(taskRes.data));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Years derived from every project's inicio date plus the current year and
  // always include 2025 / 2026 so the catalog covers the active range even
  // before any project lands in those years.
  const availableYears = useMemo(() => {
    const set = new Set();
    for (const p of projects) {
      const y = yearOf(p.inicio);
      if (y) set.add(y);
    }
    set.add(currentYear);
    set.add(2025);
    set.add(2026);
    return [...set].sort((a, b) => b - a);
  }, [projects, currentYear]);

  const visibleProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return projects.filter(p => {
      if (statusFilter !== 'all' && p.estado !== statusFilter) return false;
      if (yearFilter !== 'all' && yearOf(p.inicio) !== yearFilter) return false;
      if (q) {
        const inName = (p.nombre || '').toLowerCase().includes(q);
        const inClient = (p.cliente || '').toLowerCase().includes(q);
        if (!inName && !inClient) return false;
      }
      return true;
    });
  }, [projects, statusFilter, yearFilter, searchQuery]);

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
      <header style={isMobile ? { ...s.header, ...s.headerMobile } : s.header}>
        <div>
          <h2 style={isMobile ? { ...s.title, ...s.titleMobile } : s.title}>Cronogramas de Proyecto</h2>
          <p style={isMobile ? { ...s.subtitle, ...s.textMobile } : s.subtitle}>Selecciona un proyecto y el tipo de cronograma</p>
        </div>
        <span style={s.badge}>
          {visibleProjects.length} de {projects.length} proyectos
        </span>
      </header>

      <div style={s.searchRow}>
        <div style={s.searchWrap}>
          <span style={s.searchIcon} aria-hidden="true">🔍</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar proyecto o cliente..."
            style={isMobile ? { ...s.searchInput, ...s.searchInputMobile } : s.searchInput}
            aria-label="Buscar proyecto o cliente"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={isMobile ? { ...s.searchClearBtn, ...s.tapMobile } : s.searchClearBtn}
              aria-label="Limpiar búsqueda"
              title="Limpiar"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div style={s.filtersRow}>
        <div style={isMobile ? { ...s.tabs, ...s.tabsMobile } : s.tabs}>
          {STATUS_TABS.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setStatusFilter(t.value)}
              style={isMobile ? { ...s.tab, ...s.tabMobile, ...(statusFilter === t.value ? s.tabActive : {}) } : { ...s.tab, ...(statusFilter === t.value ? s.tabActive : {}) }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label style={s.yearWrap}>
          <span style={s.yearLabel}>Año</span>
          <select
            value={yearFilter}
            onChange={(e) => {
              const v = e.target.value;
              setYearFilter(v === 'all' ? 'all' : Number(v));
            }}
            style={isMobile ? { ...s.yearSelect, ...s.tapMobile } : s.yearSelect}
          >
            <option value="all">Todos</option>
            {availableYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div style={s.loadingMsg}>Cargando proyectos...</div>
      ) : projects.length === 0 ? (
        <div style={s.loadingMsg}>No hay proyectos. Crea uno desde el Dashboard.</div>
      ) : visibleProjects.length === 0 ? (
        <div style={s.loadingMsg}>
          {searchQuery.trim()
            ? 'No se encontraron proyectos.'
            : 'No hay proyectos que coincidan con los filtros seleccionados.'}
        </div>
      ) : (
        <div style={isMobile ? { ...s.grid, ...s.gridMobile } : isTablet ? { ...s.grid, ...s.gridTablet } : s.grid}>
          {visibleProjects.map(p => {
            const progreso = getProjectProgressBreakdown(p, taskProgressMap).total;
            return (
            <div
              key={p.id}
              style={isMobile ? { ...s.card, ...s.cardMobile } : s.card}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 24px rgba(107,28,42,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={s.cardTop}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={s.projectName}>{p.nombre}</h3>
                  <p style={isMobile ? { ...s.clientName, ...s.textMobile } : s.clientName}>{p.cliente || '—'}</p>
                </div>
                <span style={{ ...s.statusBadge, ...getStatusStyle(p.estado) }}>{p.estado}</span>
              </div>

              <div style={s.progressSection}>
                <div style={s.progressHeader}>
                  <span style={s.progressLabel}>Progreso</span>
                  <span style={s.progressValue}>{progreso}%</span>
                </div>
                <div style={s.progressBar}>
                  <div style={{
                    ...s.progressFill,
                    width: `${progreso}%`,
                    background: getProgressColor(progreso),
                  }} />
                </div>
              </div>

              <div style={s.cardActions}>
                <button
                  style={isMobile ? { ...s.actionBtn, ...s.actionBtnMobile, background: '#6B1C2A', color: '#fff' } : { ...s.actionBtn, background: '#6B1C2A', color: '#fff' }}
                  onClick={() => onOpen && onOpen(p.id, 'diseño')}
                  onMouseEnter={e => e.currentTarget.style.background = '#561624'}
                  onMouseLeave={e => e.currentTarget.style.background = '#6B1C2A'}
                >
                  <span style={s.btnIcon}>📐</span>
                  <span>Cronograma de Diseño</span>
                </button>
                <button
                  style={isMobile ? { ...s.actionBtn, ...s.actionBtnMobile, background: '#3E2A1A', color: '#fff' } : { ...s.actionBtn, background: '#3E2A1A', color: '#fff' }}
                  onClick={() => onOpen && onOpen(p.id, 'obra')}
                  onMouseEnter={e => e.currentTarget.style.background = '#2A1C10'}
                  onMouseLeave={e => e.currentTarget.style.background = '#3E2A1A'}
                >
                  <span style={s.btnIcon}>🏗️</span>
                  <span>Cronograma de Obra</span>
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { padding: '32px 36px', maxWidth: 1400 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },
  badge: { fontSize: 11, fontWeight: 600, color: 'var(--wine)', background: 'rgba(107,28,42,0.06)', padding: '5px 14px', borderRadius: 20, alignSelf: 'center' },

  searchRow: { marginBottom: 12 },
  searchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
    padding: '0 12px',
    maxWidth: 480,
  },
  searchIcon: { fontSize: 13, opacity: 0.55, marginRight: 8 },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    padding: '10px 0', fontSize: 13,
    fontFamily: "'DM Sans', sans-serif", color: 'var(--text-primary)',
  },
  searchClearBtn: {
    border: 'none', background: 'transparent',
    fontSize: 18, lineHeight: 1, color: 'var(--text-light)',
    cursor: 'pointer', padding: '0 4px', marginLeft: 6,
  },

  filtersRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: 16, flexWrap: 'wrap', marginBottom: 18,
  },
  tabs: {
    display: 'inline-flex', gap: 2, padding: 3,
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
  },
  tab: {
    padding: '7px 14px', border: 'none', background: 'transparent',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    color: 'var(--text-secondary)', fontFamily: "'DM Sans', sans-serif",
    borderRadius: 7, transition: 'all 0.15s',
  },
  tabActive: { background: '#6B1C2A', color: '#fff' },

  yearWrap: { display: 'inline-flex', alignItems: 'center', gap: 8 },
  yearLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  yearSelect: {
    padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', fontSize: 13, fontWeight: 600,
    color: 'var(--wine)', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", fontVariantNumeric: 'tabular-nums',
  },

  loadingMsg: { background: '#fff', borderRadius: 14, border: '1px solid var(--border)', padding: '40px 24px', textAlign: 'center', color: 'var(--text-light)', fontSize: 14 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 18 },
  card: { background: '#fff', borderRadius: 14, border: '1px solid var(--border)', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16, transition: 'transform 0.2s, box-shadow 0.2s' },

  cardTop: { display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between' },
  projectName: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 600, color: 'var(--wine)', margin: 0, lineHeight: 1.2 },
  clientName: { fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0', fontWeight: 500 },
  statusBadge: { display: 'inline-block', padding: '4px 11px', borderRadius: 20, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.04em', textTransform: 'uppercase' },

  progressSection: { display: 'flex', flexDirection: 'column', gap: 6 },
  progressHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  progressLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  progressValue: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  progressBar: { height: 8, background: '#F0EBE3', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },

  cardActions: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 },
  actionBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '10px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
    transition: 'background 0.2s',
  },
  btnIcon: { fontSize: 14 },

  /* Mobile (< 640px) / tablet overrides — only merged when the breakpoint flag is set. */
  headerMobile: { flexWrap: 'wrap', gap: 10 },
  titleMobile: { fontSize: 26 },
  textMobile: { fontSize: 15 },
  searchInputMobile: { minHeight: TAP_TARGET },
  tapMobile: { minHeight: TAP_TARGET, minWidth: TAP_TARGET },
  tabsMobile: { display: 'flex', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxSizing: 'border-box' },
  tabMobile: { minHeight: TAP_TARGET, flexShrink: 0, whiteSpace: 'nowrap', fontSize: 13 },
  gridMobile: { gridTemplateColumns: 'minmax(0, 1fr)', gap: 14 },
  gridTablet: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  cardMobile: { padding: '18px 16px', minWidth: 0 },
  actionBtnMobile: { minHeight: TAP_TARGET, fontSize: 15 },
};
